import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  RefreshCw, Database, HardDrive, Cpu, Download, Upload,
  Shield, Trash2, Activity, Clock, Server, RotateCcw
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemSettings() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(null);
  const fileInputRef = useRef(null);

  const authHeaders = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token]);

  const fetchData = async () => {
    try {
      const [statsRes, healthRes, backupsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/system/stats`, authHeaders),
        axios.get(`${BACKEND_URL}/api/system/health`, authHeaders),
        axios.get(`${BACKEND_URL}/api/system/backups`, authHeaders)
      ]);
      setStats(statsRes.data);
      setHealth(healthRes.data);
      setBackups(backupsRes.data.backups || []);
    } catch (error) {
      toast.error('خطأ في تحميل البيانات');
    } finally {
      setLoading(false);
    }
  };

  const createBackup = async () => {
    setBackupLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/system/backup`, {}, authHeaders);
      toast.success('تم إنشاء النسخة الاحتياطية بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في إنشاء النسخة الاحتياطية');
    } finally {
      setBackupLoading(false);
    }
  };

  const downloadBackup = async (backup) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/system/backup/${backup.id}/download`,
        { ...authHeaders, responseType: 'blob' }
      );
      
      // Detect JSON-error response wrapped in blob
      if (response.data && response.data.type && response.data.type.includes('json')) {
        const text = await response.data.text();
        try {
          const err = JSON.parse(text);
          toast.error(err.detail || 'تعذّر تنزيل النسخة الاحتياطية');
        } catch {
          toast.error('تعذّر تنزيل النسخة الاحتياطية');
        }
        return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', backup.filename || `backup_${backup.id}.json.gz`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم تحميل النسخة الاحتياطية');
    } catch (error) {
      // Parse the blob error if axios threw
      try {
        if (error.response && error.response.data instanceof Blob) {
          const text = await error.response.data.text();
          const err = JSON.parse(text);
          toast.error(err.detail || 'خطأ في تحميل النسخة الاحتياطية');
          return;
        }
      } catch {}
      toast.error('خطأ في تحميل النسخة الاحتياطية');
    }
  };

  const restoreBackup = async (backupId) => {
    if (!window.confirm('هل أنت متأكد من استعادة هذه النسخة؟ سيتم استبدال البيانات الحالية.')) {
      return;
    }
    
    setRestoreLoading(backupId);
    try {
      await axios.post(
        `${BACKEND_URL}/api/system/backup/${backupId}/restore?confirm=true`,
        {},
        authHeaders
      );
      toast.success('تم استعادة النسخة الاحتياطية بنجاح');
      fetchData();
    } catch (error) {
      toast.error('خطأ في استعادة النسخة الاحتياطية');
    } finally {
      setRestoreLoading(null);
    }
  };

  const handleImportBackup = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.loading('جاري استيراد النسخة الاحتياطية...');
      await axios.post(
        `${BACKEND_URL}/api/system/backup/import`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.dismiss();
      toast.success('تم استيراد النسخة الاحتياطية بنجاح');
      fetchData();
    } catch (error) {
      toast.dismiss();
      toast.error('خطأ في استيراد النسخة الاحتياطية');
    }
    
    // Reset file input
    event.target.value = '';
  };

  const clearCache = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/system/cache/clear`, {}, authHeaders);
      toast.success('تم مسح الكاش بنجاح');
    } catch (error) {
      toast.error('خطأ في مسح الكاش');
    }
  };

  const reindexDatabase = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/system/database/reindex`, {}, authHeaders);
      toast.success('تم إعادة إنشاء الفهارس بنجاح');
    } catch (error) {
      toast.error('خطأ في إنشاء الفهارس');
    }
  };

  const formatBytes = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('ar-SA');
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" dir="rtl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">إعدادات النظام</h1>
          <p className="text-gray-500">إدارة النظام والنسخ الاحتياطي</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 ml-2" />
          تحديث
        </Button>
      </div>

      {/* Health Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className={`${health?.status === 'healthy' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Activity className={`w-8 h-8 ${health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`} />
              <div>
                <p className="text-sm text-gray-600">حالة النظام</p>
                <p className={`text-lg font-bold ${health?.status === 'healthy' ? 'text-green-600' : 'text-red-600'}`}>
                  {health?.status === 'healthy' ? 'يعمل بشكل طبيعي' : 'يوجد مشكلة'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Cpu className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">استخدام الذاكرة</p>
                <p className="text-lg font-bold text-blue-600">
                  {stats?.system?.memory_percent?.toFixed(1)}%
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-purple-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <HardDrive className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">مساحة القرص المتاحة</p>
                <p className="text-lg font-bold text-purple-600">
                  {stats?.system?.disk_free_gb?.toFixed(1)} GB
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8 text-amber-600" />
              <div>
                <p className="text-sm text-gray-600">إجمالي السجلات</p>
                <p className="text-lg font-bold text-amber-600">
                  {stats?.total_documents?.toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Collections Stats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Database className="w-5 h-5" />
              إحصائيات البيانات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats?.collections && Object.entries(stats.collections).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <span className="text-gray-700">{value.name}</span>
                  <span className="font-bold text-blue-600">{value.count?.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* System Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              إجراءات النظام
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg">
              <h3 className="font-semibold text-green-800 mb-2">النسخ الاحتياطي</h3>
              <p className="text-sm text-green-600 mb-3">إنشاء نسخة احتياطية من جميع البيانات</p>
              <div className="flex gap-2">
                <Button 
                  onClick={createBackup} 
                  disabled={backupLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {backupLoading ? (
                    <RefreshCw className="w-4 h-4 ml-2 animate-spin" />
                  ) : (
                    <Shield className="w-4 h-4 ml-2" />
                  )}
                  إنشاء نسخة احتياطية
                </Button>
              </div>
            </div>

            <div className="p-4 bg-violet-50 rounded-lg">
              <h3 className="font-semibold text-violet-800 mb-2">استيراد نسخة احتياطية</h3>
              <p className="text-sm text-violet-600 mb-3">استيراد نسخة احتياطية من ملف</p>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImportBackup}
                accept=".json,.gz"
                className="hidden"
              />
              <Button 
                onClick={() => fileInputRef.current?.click()}
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-100"
              >
                <Upload className="w-4 h-4 ml-2" />
                استيراد من ملف
              </Button>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h3 className="font-semibold text-blue-800 mb-2">تحسين الأداء</h3>
              <p className="text-sm text-blue-600 mb-3">إعادة إنشاء فهارس قاعدة البيانات</p>
              <Button 
                onClick={reindexDatabase}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-100"
              >
                <Database className="w-4 h-4 ml-2" />
                إعادة إنشاء الفهارس
              </Button>
            </div>

            <div className="p-4 bg-orange-50 rounded-lg">
              <h3 className="font-semibold text-orange-800 mb-2">مسح الكاش</h3>
              <p className="text-sm text-orange-600 mb-3">مسح البيانات المخزنة مؤقتاً</p>
              <Button 
                onClick={clearCache}
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                <Trash2 className="w-4 h-4 ml-2" />
                مسح الكاش
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Backups List */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            النسخ الاحتياطية ({backups.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>لا توجد نسخ احتياطية</p>
              <p className="text-sm">اضغط على &quot;إنشاء نسخة احتياطية&quot; لإنشاء أول نسخة</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="p-3 text-right">التاريخ</th>
                    <th className="p-3 text-center">النوع</th>
                    <th className="p-3 text-center">السجلات</th>
                    <th className="p-3 text-center">الحجم</th>
                    <th className="p-3 text-center">الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup) => (
                    <tr key={backup.id} className="border-b hover:bg-gray-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-400" />
                          {formatDate(backup.created_at)}
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        <span className={`px-2 py-1 rounded text-xs ${
                          backup.type === 'auto' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {backup.type === 'auto' ? 'تلقائي' : 'يدوي'}
                        </span>
                      </td>
                      <td className="p-3 text-center font-medium">
                        {backup.total_documents?.toLocaleString()}
                      </td>
                      <td className="p-3 text-center text-gray-600">
                        {formatBytes(backup.file_size)}
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-blue-600 hover:bg-blue-50"
                            onClick={() => downloadBackup(backup)}
                            title="تحميل"
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-green-600 hover:bg-green-50"
                            onClick={() => restoreBackup(backup.id)}
                            disabled={restoreLoading === backup.id}
                            title="استعادة"
                          >
                            {restoreLoading === backup.id ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <RotateCcw className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
